import * as React from 'react';
import {
    CartItem,
    ShoppingCart,
    ShoppingCartButton,
    ShoppingCartContext,
    useOutsideClickHandler,
    Global,
    hotelUtils,
} from '@weddingspot/ws-core-components';
import { SearchContext } from './SearchContext';
import { CSNSearchVenueData } from '@weddingspot/ws-api-client';

import * as analytics from '@weddingspot/ws-analytics';

import { HotelIcon } from '@cvent/carina/components/Icon';

const styles = require('./HotelShoppingCart.module.scss');

interface HotelShoppingCartProps {
    onClickSubmit: () => void;
}

const _searchResultToCartItem = (res: CSNSearchVenueData, isRecommendation: boolean): CartItem => {
    let rating = hotelUtils.getHotelRatingFromCsnFormat(res.preferredRating);
    let ratingProps;
    if (rating && rating.type && rating.value) {
        ratingProps = rating as { type: 'DIAMONDS' | 'STARS'; value: number };
    }

    return {
        id: res.id,
        name: res.name,
        location: `${res.city}, ${res.stateProvinceCode}`,
        imageUrl: res.mainImage ? res.mainImage.small : undefined,
        rating: ratingProps,
        isRecommendation,
        lastUpdated: new Date().getTime(),
    };
};

export const HotelShoppingCart = (props: HotelShoppingCartProps) => {
    const { cartOpen, setCartOpen, recommendationsSeen, items, addItem, itemsMax } = React.useContext(ShoppingCartContext);
    const shoppingCartContainerRef = React.useRef<HTMLDivElement>(null);
    const { searchResults } = React.useContext(SearchContext);

    // On click outside of shopping cart, close it
    useOutsideClickHandler(shoppingCartContainerRef, () => setCartOpen(false));

    /* Automatically add up to 2 recommendations to the cart if the following conditions are met:
     *   1. There's room in the cart
     *   2. There are venues to recommend that are not already in the cart
     *   3. Recommendations haven't already been added/seen
     *   4. The user is adding their 1st item to the cart
     */

    /*
    temporary commented via WS-1103
    React.useEffect(
        () => {
            if (!searchResults || recommendationsSeen || (items.length !== 1)) { return; }
            
            const addable = searchResults
                .filter(r => !items.find(i => i.id === r.id))
                .sort((a, b) => compareDiamondRatings(a.diamondLevel, b.diamondLevel));

            if (addable.length > 0 && items.length < itemsMax) {
                addItem(_searchResultToCartItem(addable[0], true));
            }
            if (addable.length > 1 && items.length + 1 < itemsMax) {
                addItem(_searchResultToCartItem(addable[1], true));
            }
            if (addable.length > 0) {
                setCartOpen(true);
            }
            
        },
        [items.length, recommendationsSeen, searchResults]
    );
    */

    React.useEffect(() => {
        if (cartOpen) {
            analytics.setCurUserProps({ name: analytics.ClarityTags.HOTELS_SHOPPING_CARD, value: 'Yes' });
        }
    }, [cartOpen]);

    return (
        <div className={styles.shoppingCartContainer} ref={shoppingCartContainerRef}>
            <ShoppingCartButton itemDescriptor={(count: number) => `${count} ${count === 1 ? 'hotel' : 'hotels'}`} carinaIcon={HotelIcon} />
            {cartOpen && (
                <div className={styles.shoppingCartList}>
                    <Global preventScrolling='onlyMobile' />
                    <ShoppingCart
                        itemsMax={10}
                        title={'Hotel List'}
                        subtitle={'Add the best hotels near your ' + 'wedding venue to contact them about a room block proposal.'}
                        // temporary commented via WS-1103
                        // recommendationsTitle={'Here are some more places you may love:'}
                        addHelpText={(count: number) => `Add up to ${count} more hotels.`}
                        addCTAText={'Find more hotels'}
                        onClickAddCTA={() => Promise.resolve(false)}
                        submitCTAText={'Send request'}
                        submitCTASubtitle={'Tell us about your stay. Hotels will respond with a personalized quote.'}
                        onClickSubmitCTA={() => {
                            props.onClickSubmit();
                            return Promise.resolve(false);
                        }}
                    />
                </div>
            )}
        </div>
    );
};
